import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useAuthStore } from "../stores/auth-store";
import {
  fetchProfiles,
  fetchAccessControls,
  fetchFieldControls,
  fetchMasterControls,
  fetchShortcuts,
  fetchStores,
  fetchSalesConfig,
  fetchMtypeMaster,
  fetchPrintConfig,
} from "../api/config-api";
import { SIDEBAR_TITLE, MASTER_UI, MASTER_TABLE } from "../config/static";
import type {
  MtypeMaster,
  Profile,
  AccessControlRule,
  FieldControlRule,
  SalesConfig,
  SidebarItem,
} from "../types/auth";
import type {
  CmMasterControl,
  CmShortcut,
  CmStoreDirectoryEntry,
  ControlManagementData,
} from "../types/control-management";

const CONFIG_STALE_TIME = 30 * 60 * 1000; // 30 minutes

export const configKeys = {
  all: ["config"] as const,
  profiles: (shopId: string) => ["config", "profiles", shopId] as const,
  accessControl: (shopId: string) =>
    ["config", "accessControl", shopId] as const,
  fieldControl: ["config", "fieldControl"] as const,
  sidebarTitle: ["config", "sidebarTitle"] as const,
  stores: (shopId: string) => ["config", "stores", shopId] as const,
  masterControl: ["config", "masterControl"] as const,
  mtype_master: (shopId: string) => ["config", "mtype_master", shopId] as const,
  loyalityconfig: (shopId: string) =>
    ["config", "loyalityconfig", shopId] as const,
  print_conf: (shopId: string) => ["config", "print_conf", shopId] as const,
  shortcuts: ["config", "shortcuts"] as const,
  colormaster: (shopId: string) => ["config", "colormaster", shopId] as const,
  salesconfig: (shopId: string) => ["config", "salesconfig", shopId] as const,
};

export const cmKeys = {
  all: ["cm"] as const,
  profiles: (shopId: string) => ["cm", "profiles", shopId] as const,
  accessControl: (shopId: string) => ["cm", "accessControl", shopId] as const,
  fieldControl: ["cm", "fieldControl"] as const,
  masterControl: ["cm", "masterControl"] as const,
  shortcuts: ["cm", "shortcuts"] as const,
  stores: (shopId: string) => ["cm", "stores", shopId] as const,
};

// --- Typed query hooks ---

export function useProfiles(): Profile[] {
  const shopId = useAuthStore((s) => s.store?.shopId ?? "");
  const { data } = useQuery<Profile[]>({
    queryKey: configKeys.profiles(shopId),
    queryFn: () => fetchProfiles<Profile[]>(shopId),
    staleTime: CONFIG_STALE_TIME,
    enabled: !!shopId,
  });
  return data ?? [];
}

export function useAccessControl(): AccessControlRule[] {
  const shopId = useAuthStore((s) => s.store?.shopId ?? "");
  const { data } = useQuery<AccessControlRule[]>({
    queryKey: configKeys.accessControl(shopId),
    queryFn: () => fetchAccessControls<AccessControlRule[]>(shopId),
    staleTime: CONFIG_STALE_TIME,
    enabled: !!shopId,
  });
  return data ?? [];
}

export function useFieldControl(): FieldControlRule[] {
  const { data } = useQuery<FieldControlRule[]>({
    queryKey: configKeys.fieldControl,
    queryFn: () => fetchFieldControls<FieldControlRule[]>(),
    staleTime: CONFIG_STALE_TIME,
  });
  return data ?? [];
}

/**
 * Resolves the current user's access level from their profile.
 * Matches `user.userType` against the profiles list to find
 * the corresponding `accessLevel` (e.g. "mdm", "shop").
 */
export function useAccessLevel(): string {
  const profiles = useProfiles();
  const userType = useAuthStore((s) => s.user?.userType);
  return useMemo(() => {
    if (!userType) return "mdm";
    const profile = profiles.find(
      (p) => p.profile.toLowerCase() === userType.toLowerCase(),
    );
    return profile?.accessLevel?.toLowerCase() ?? "mdm";
  }, [profiles, userType]);
}

export function useSidebarConfig(): Record<string, SidebarItem> {
  return SIDEBAR_TITLE;
}

export function useSalesConfig(): SalesConfig | undefined {
  const shopId = useAuthStore((s) => s.store?.shopId ?? "");
  const { data } = useQuery<SalesConfig[]>({
    queryKey: configKeys.salesconfig(shopId),
    queryFn: () => fetchSalesConfig<SalesConfig[]>(shopId),
    staleTime: CONFIG_STALE_TIME,
    enabled: !!shopId,
  });
  return data?.[0];
}

export function useMtypeMaster(): Record<string, MtypeMaster> {
  const shopId = useAuthStore((s) => s.store?.shopId ?? "");
  const { data } = useQuery<MtypeMaster[]>({
    queryKey: configKeys.mtype_master(shopId),
    queryFn: () => fetchMtypeMaster<MtypeMaster[]>(shopId),
    staleTime: CONFIG_STALE_TIME,
    enabled: !!shopId,
  });
  const map = useMemo(() => {
    const m: Record<string, MtypeMaster> = {};
    for (const entry of data ?? []) {
      if (entry.movementType) m[entry.movementType] = entry;
    }
    return m;
  }, [data]);
  return map;
}

export function usePrintConf(): Record<string, unknown>[] {
  const shopId = useAuthStore((s) => s.store?.shopId ?? "");
  const { data } = useQuery<Record<string, unknown>[]>({
    queryKey: configKeys.print_conf(shopId),
    queryFn: () => fetchPrintConfig<Record<string, unknown>[]>(shopId),
    staleTime: CONFIG_STALE_TIME,
    enabled: !!shopId,
  });
  return data ?? [];
}

/**
 * Returns true once the critical config data has loaded.
 * Sidebar is now bundled in the FE, so this is always true.
 */
export function useConfigLoaded(): boolean {
  return true;
}

// --- Control Management composite loader ---

export interface UseControlManagementData {
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  data: ControlManagementData | null;
}

/**
 * Composes the six v2 /access-control reads with the three bundled
 * static JSONs. Parallel fetch via independent useQuery calls; each
 * sub-key can be invalidated in isolation by mutations.
 */
export function useControlManagementData(): UseControlManagementData {
  const shopId = useAuthStore((s) => s.store?.shopId ?? "");

  const profilesQ = useQuery<Profile[]>({
    queryKey: cmKeys.profiles(shopId),
    queryFn: () => fetchProfiles<Profile[]>(shopId),
    staleTime: CONFIG_STALE_TIME,
    enabled: !!shopId,
  });
  const accessQ = useQuery<AccessControlRule[]>({
    queryKey: cmKeys.accessControl(shopId),
    queryFn: () => fetchAccessControls<AccessControlRule[]>(shopId),
    staleTime: CONFIG_STALE_TIME,
    enabled: !!shopId,
  });
  const fieldsQ = useQuery<FieldControlRule[]>({
    queryKey: cmKeys.fieldControl,
    queryFn: () => fetchFieldControls<FieldControlRule[]>(),
    staleTime: CONFIG_STALE_TIME,
  });
  const mastersQ = useQuery<CmMasterControl[]>({
    queryKey: cmKeys.masterControl,
    queryFn: () => fetchMasterControls<CmMasterControl[]>(),
    staleTime: CONFIG_STALE_TIME,
  });
  const shortcutsQ = useQuery<CmShortcut[]>({
    queryKey: cmKeys.shortcuts,
    queryFn: () => fetchShortcuts<CmShortcut[]>(),
    staleTime: CONFIG_STALE_TIME,
  });
  const storesQ = useQuery<CmStoreDirectoryEntry[]>({
    queryKey: cmKeys.stores(shopId),
    queryFn: () => fetchStores<CmStoreDirectoryEntry[]>(shopId),
    staleTime: CONFIG_STALE_TIME,
    enabled: !!shopId,
  });

  const queries = [profilesQ, accessQ, fieldsQ, mastersQ, shortcutsQ, storesQ];
  const isLoading = queries.some((q) => q.isLoading);
  const firstError = queries.find((q) => q.isError)?.error ?? null;
  const isError = firstError !== null;

  const data = useMemo<ControlManagementData | null>(() => {
    if (
      !profilesQ.data ||
      !accessQ.data ||
      !fieldsQ.data ||
      !mastersQ.data ||
      !shortcutsQ.data ||
      !storesQ.data
    ) {
      return null;
    }
    return {
      profiles: profilesQ.data,
      accessControl: accessQ.data,
      fieldControl: fieldsQ.data,
      masterControl: mastersQ.data,
      shortcuts: shortcutsQ.data,
      stores: storesQ.data,
      sidebarTitle: SIDEBAR_TITLE,
      masters: MASTER_UI,
      mastersTable: MASTER_TABLE,
    };
  }, [
    profilesQ.data,
    accessQ.data,
    fieldsQ.data,
    mastersQ.data,
    shortcutsQ.data,
    storesQ.data,
  ]);

  return {
    isLoading,
    isError,
    error: firstError instanceof Error ? firstError : null,
    data,
  };
}
